import React, { createContext, useState, useContext } from "react";
import { ToastContainer } from 'react-toastify';
import axios from "axios";

import CampaignIndex from "./CampaignIndex";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import { initialCampaignsSource } from "./campaignsSource";
import SetupModal from "../MarketingSettings/SetupModal";

const CampaignsContext = createContext();

export function useCampaignsContext() {
  return useContext(CampaignsContext);
}

const CampaignsProvider = (props) => {
  const {
    csrfToken,
    user,
    team,
    teamMembership,
    enforceMarketingVenueAccessControl,
    campaignTemplates,
    customCampaigns,
    customCampaignTemplates,
    aiAutomationBuilder,
    venueOwnerships,
    sendDomains,
    dataTableProps
  } = props;
  const { dataSource, ...modifiedDataTableProps } = dataTableProps;

  const [venueOwnership, setVenueOwnership] = useState(props.venueOwnership);
  const [activeCampaigns, setActiveCampaigns] = useState([]);
  const [dataTableSource, setDataTableSource] = useState(() => initialCampaignsSource(dataSource));
  const [dataTableRefreshedAt, setDataTableRefreshedAt] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);
  const [campaignCount, setCampaignCount] = useState(0);

  const refreshDataTable = () => {
    setDataTableRefreshedAt(new Date());
  };

  const updateCampaign = (campaignId, data, onSuccess = () => {}) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.put("/teams/" + team.id + "/campaigns/" + campaignId, data)
      .then(({data}) => {
        setActiveCampaigns(current => {
          return current.map(campaign => {
            if (campaign.id === campaignId) {
              return data;
            }

            return campaign;
          });
        });
        toastSuccess("Automation has been updated");
        onSuccess();
      })
      .catch(_error => toastError("Could not update automation, please try again"));
  };

  const updateCustomCampaign = (customCampaignId, data, onSuccess = () => {}) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.put("/teams/" + team.id + "/custom_campaigns/" + customCampaignId, data)
      .then(({data}) => {
        toastSuccess("Automation has been updated");
        onSuccess();
      })
      .catch(_error => toastError("Could not update automation, please try again"));
  };

  const deleteCustomCampaign = (customCampaignId, onSuccess = () => {}) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.delete(`/teams/${team.id}/custom_campaigns/${customCampaignId}`)
      .then(() => {
        toastSuccess("Automation has been deleted");
        onSuccess();
      })
      .catch(_error => toastError("Could not delete automation, please try again"));
  };

  const moveCustomCampaign = (customCampaignId, venueOwnershipId, onSuccess = () => {}) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.put(`/teams/${team.id}/custom_campaigns/${customCampaignId}`, {
      custom_campaign: { venue_ownership_id: venueOwnershipId }
    })
      .then(() => {
        toastSuccess("Automation venue has been changed");
        onSuccess();
      })
      .catch(_error => toastError("Could not change automation venue, please try again"));
  };

  const startCampaign = (campaignTemplate) => {
    if (campaignTemplate.campaign) {
      window.location.href = `/teams/${team.id}/campaigns/${campaignTemplate.campaign.id}`;
    } else {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      axios.post("/teams/" + team.id + "/campaigns/", {
        campaign: {
          state: "available",
          campaign_template_id: campaignTemplate.id,
          team_id: team.id,
          venue_ownership_id: venueOwnership.id
        }
      })
        .then(({ data }) => {
          window.location.href = `/teams/${team.id}/campaigns/${data.id}`;
        })
        .catch(_error => toastError("Could not start automation, please try again"));
    };
  };

  const copyCampaign = (campaignTemplate) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.post(`/teams/${team.id}/custom_campaigns/create_from_template`, {
      id: campaignTemplate.id,
    })
      .then(({ data }) => {
        window.location.href = `/teams/${team.id}/custom_campaigns/${data.id}`;
      })
      .catch(_error => toastError("Could not copy automation, please try again"));
  };

  const value = {
    csrfToken,
    user,
    team,
    teamMembership,
    enforceMarketingVenueAccessControl,
    campaignTemplates,
    customCampaignTemplates,
    aiAutomationBuilder,
    updateCampaign,
    startCampaign,
    copyCampaign,
    venueOwnerships,
    venueOwnership,
    setVenueOwnership,
    isLoading,
    setIsLoading,
    activeCampaigns,
    dataTableProps,
    updateCustomCampaign,
    deleteCustomCampaign,
    moveCustomCampaign,
    customCampaigns,
    customCampaignTemplates,
    dataSource,
    dataTableSource,
    setDataTableSource,
    modifiedDataTableProps,
    dataTableRefreshedAt,
    setDataTableRefreshedAt,
    refreshDataTable,
    campaignCount,
    setCampaignCount,
  };

  return (
    <CampaignsContext.Provider value={value}>
      <>
        <SetupModal
          venueOwnership={venueOwnership}
          sendDomains={sendDomains}
        />
        {props.children}
      </>
    </CampaignsContext.Provider>
  );
};

const CampaignsContainer = (props) => (
  <CampaignsProvider {...props}>
      <ToastContainer icon={false} />
    <CampaignIndex />
  </CampaignsProvider>
);

export default CampaignsContainer;
